import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPen } from 'react-icons/fa';
import { Button } from 'react-bootstrap';
import styles from './IncidentDetailHeader.module.css';
import { getIncidentStatusText, formatTimestamp, isResolvedStatus } from '../Status/statusUtils';
import { hasEditorAccess } from '../../../utils/privilegeHelpers';
import { trackMissingResolveDate } from '../../../utils/modernUITracking';

/**
 * IncidentDetailHeader
 *
 * Displays the top header section of the Modern incident detail page.
 * Shows the 6 AC fields: Priority badge, Impacted Brands, Incident Status,
 * Client Impact, Started Time, and Next Update.
 *
 * This is a pure display component — all data fetching happens in the parent.
 *
 * @param {Object}      props
 * @param {string}      props.shortDescription      - Incident title
 * @param {string}      props.priority              - "EP1"–"EP4"
 * @param {string}      props.status                - Raw status string from backend
 * @param {string}      props.affectedBrand         - Pre-formatted brand string (e.g. "Manheim, AutoTrader")
 * @param {string}      props.clientImpact          - Client impact text (outageData.impact) shown in impact box
 * @param {number|null} props.incidentStart         - Unix timestamp (ms or s; auto-normalized by formatTimestamp).
 *                                                    Displayed as "Started ..." while the incident is open.
 * @param {number|null} props.incidentEnd           - Resolve-time Unix timestamp (ms or s). Displayed as
 *                                                    "Resolved ..." once the status is "Resolve"; renders
 *                                                    "Resolved —" when the incident is resolved but this is
 *                                                    unset or does not parse to a date.
 * @param {number|null} props.nextUpdate            - Unix timestamp (ms or s; auto-normalized), or 0/null
 * @param {boolean}     props.loading               - Show skeleton placeholders while data loads
 * @param {string}      props.statusFullDescription - Long-form outage description (outageData.description); shown
 *                                                    in the Current Status box. Preferred over statusDescription.
 * @param {string}      props.statusDescription     - Short outage description (outageData.short_description);
 *                                                    fallback when statusFullDescription is absent or blank.
 */
const IncidentDetailHeader = ({
  shortDescription = 'No description available',
  priority = 'EP4',
  status = '',
  affectedBrand = '',
  clientImpact = '',
  incidentStart = null,
  incidentEnd = null,
  nextUpdate = null,
  loading = false,
  user = null,
  problemId = '',
  statusFullDescription = '',
  statusDescription = '',
}) => {
  const navigate = useNavigate();
  const normalizedPriority = priority?.toUpperCase() || 'EP4';
  const statusText = getIncidentStatusText(status);

  // Format "Started Feb 17, 8:23 AM" — or "Resolved ..." once resolved.
  // PRB statuses are "Log & Classify", "Diagnose", "Resolve" — backend sends "Resolve"
  // for closed incidents (shared predicate: statusUtils.isResolvedStatus).
  //
  // US2072496: when resolved this now renders incidentEnd. It previously flipped the
  // label to "Resolved" while still formatting incidentStart, so a resolved incident
  // showed its start time labelled as its resolve time. A resolved incident with no
  // incidentEnd renders "Resolved —" so the missing timestamp is visible and gets
  // investigated rather than being masked by the start time.
  // "Missing" is keyed off the formatted result rather than off `!incidentEnd`, so a
  // truthy but unparseable timestamp — which formatTimestamp turns into '' — renders
  // "Resolved —" and emits the signal too, instead of a bare "Resolved ".
  const isResolved = isResolvedStatus(status);
  const formattedResolveTime = isResolved
    ? formatTimestamp(incidentEnd, { relative: false, includeTime: true })
    : '';

  const formattedIncidentDate = isResolved
    ? `Resolved ${formattedResolveTime || '—'}`
    : incidentStart
      ? `Started ${formatTimestamp(incidentStart, { relative: false, includeTime: true })}`
      : null;

  // Emit the data-defect signal once per affected incident rather than per render.
  // Gated on !loading so the initial render (status still empty) cannot false-fire.
  const missingResolveDate = !loading && isResolved && !formattedResolveTime;
  useEffect(() => {
    if (missingResolveDate) {
      trackMissingResolveDate(problemId, { surface: 'detail_header' });
    }
  }, [missingResolveDate, problemId]);

  // Next update — backend stores 0 to mean "not set"
  const hasNextUpdate = nextUpdate && Number(nextUpdate) > 0;
  const nextUpdateText = hasNextUpdate
    ? formatTimestamp(nextUpdate, { relative: false, includeTime: true })
    : 'As more information becomes available';

  // Outage description text for the Current Status box
  const currentStatusText = statusFullDescription?.trim() || statusDescription?.trim() || '';

  // Map priority to CSS modifier class (e.g. priorityEP1)
  const priorityClass = styles[`priority${normalizedPriority}`] || '';

  if (loading) {
    return (
      <div className={styles.header} aria-busy="true" aria-label="Loading incident details">
        <div className={styles.skeletonTitle} />
        <div className={styles.skeletonMeta} />
        <div className={styles.skeletonBox} />
        <div className={styles.skeletonNextUpdate} />
      </div>
    );
  }

  return (
    <div className={styles.header}>
      {/* Incident title */}
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{shortDescription}</h1>
        {/* Edit action (right-aligned) */}
        {hasEditorAccess(user) && problemId && (
          <Button
            variant="secondary"
            size="sm"
            className="d-inline-flex align-items-center gap-1"
            onClick={() => navigate(`/problem/edit/${problemId}`)}
            aria-label="Edit incident"
            data-testid="incident-edit-button"
          >
            <FaPen /> Edit
          </Button>
        )}
      </div>

      {/* Metadata bar: EP badge · Brand · Status · Started time · Edit action */}
      <div className={styles.metaBar} role="list" aria-label="Incident metadata">
        <div className={styles.metaItems}>
          {/* Priority badge — solid colored pill */}
          <span
            className={`${styles.priorityBadge} ${priorityClass}`}
            role="listitem"
            aria-label={`Priority ${normalizedPriority}`}
          >
            {normalizedPriority}
          </span>

          {/* Impacted brands */}
          {affectedBrand && (
            <>
              <span className={styles.metaSeparator} aria-hidden="true">·</span>
              <span className={styles.metaItem} role="listitem">
                {affectedBrand}
              </span>
            </>
          )}

          {/* Incident status */}
          {statusText && (
            <>
              <span className={styles.metaSeparator} aria-hidden="true">·</span>
              <span className={styles.metaItem} role="listitem">
                {statusText}
              </span>
            </>
          )}

          {/* Started / resolved time */}
          {formattedIncidentDate && (
            <>
              <span className={styles.metaSeparator} aria-hidden="true">·</span>
              <span className={styles.metaItem} role="listitem">
                {formattedIncidentDate}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Current status box */}
      {currentStatusText && (
        <div className={styles.currentStatusBox} role="region" aria-label="Current status">
          <span className={styles.currentStatusLabel}>Current Status</span>
          <p className={styles.currentStatusText}>{currentStatusText}</p>
        </div>
      )}

      {/* Client impact box */}
      {clientImpact && (
        <div className={styles.clientImpactBox} role="region" aria-label="Client impact">
          <span className={styles.clientImpactLabel}>Client Impact</span>
          <p className={styles.clientImpactText}>{clientImpact}</p>
        </div>
      )}

      {/* Next update */}
      {!isResolved && (
        <div className={styles.nextUpdateRow}>
          <span className={styles.nextUpdateLabel}>Next Update:</span>
          <span className={styles.nextUpdateValue}>{nextUpdateText}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(IncidentDetailHeader);
